import styles from './AssistantPanel.module.css';

import type { FC, ReactNode, ErrorInfo, MouseEvent, KeyboardEvent } from 'react';
import type { AssistantSurface, ApiAssistantSaleCar, ApiAssistantRentalCar } from 'src/types/assistant';

import classNames from 'classnames';
import { useNavigate } from 'react-router';
import { X, Sparkle, PaperPlaneTilt } from '@phosphor-icons/react';
import React, { useRef, useState, Component, useEffect, useLayoutEffect } from 'react';

import { useSession } from 'src/hooks/useSession';
import { useMediaQuery } from 'src/hooks/useMediaQuery';

import { formatMoney } from 'src/utils/money';
import { formatImageUrl } from 'src/utils/format-image';
import { offerPathWithPoint } from 'src/utils/assistantOffer';
import { rentalCarTermsLine } from 'src/utils/rentalCarTerms';
import { bookingPathWithDates } from 'src/utils/bookingDatesFromSearch';

import { useAssistantStatus } from 'src/queries';

import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';
import {
  ListingCard,
  ListingCardPhoto,
  ListingCardAmount,
  ListingCardNoPhoto,
  ListingCardPriceLabel,
} from 'src/components/ListingCard';

import { tripCostText } from './tripCost';
import { assistantRentalPrice } from './rentalPrice';
import { markReturnToAssistant } from './returnMark';
import { EvidenceCard, saleSecondaryLine } from './EvidenceCard';
import { useAssistantConversation } from './AssistantConversations';
import { ASSISTANT_TEXT_LIMIT, type ConversationEntry } from './conversationStore';

export { ASSISTANT_TEXT_LIMIT } from './conversationStore';
export { describeAssistantFailure } from './assistantFailure';

export type { AssistantFailure } from './assistantFailure';

/** The window's name: the pill says "Ask GAC AI", the window it opens is "GAC AI" (Tim, 2026-11-04). */
export const ASSISTANT_TITLE = 'GAC AI';

/** The field's name, said by its placeholder too, so what a person reads is what a screen reader says. */
export const ASSISTANT_FIELD_NAME = 'Describe what you need';

/**
 * What the window says and does on each catalog (api#425): one component, the words and the action
 * of its catalog. `promise` is the assistant's rules, the conversation's first message; `examples`
 * are two requests a person may start from (spec §3, row г) — a tap puts one into the field, it is
 * never sent by itself: every message counts against the day.
 */
const SURFACE_COPY: Record<AssistantSurface, { promise: string; examples: [string, string]; signInFrom: string; pending: string }> = {
  rent: {
    promise:
      'An AI assistant. It picks from the catalogue by evidence, hides nothing, and never books for you: you book on the car’s page.',
    examples: ['A 7-seater for next weekend', 'An automatic for a week, up to 60 a day'],
    signInFrom: '/rent',
    pending: 'Looking through the catalogue…',
  },
  buy: {
    promise:
      'An AI assistant. It picks from the catalogue by evidence, hides nothing, and never buys for you: you send the offer, pay and sign on the car’s page.',
    examples: ['An automatic SUV up to 20,000 EUR', 'A family car delivered to Berlin'],
    signInFrom: '/buy',
    pending: 'Looking through the catalogue and the evidence…',
  },
};

/** A phone's keyboard: Enter is a new line there, and the round button sends (spec §3, row е). */
const TOUCH_QUERY = '(pointer: coarse)';

const rentalSecondaryLine = (car: ApiAssistantRentalCar): string | undefined => {
  const parts = [
    car.location?.trim() || undefined,
    rentalCarTermsLine({ seats: car.seats, dailyKmLimit: car.dailyKmLimit, dailyKmUnlimited: car.dailyKmUnlimited }),
  ].filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(' · ') : undefined;
};

/** The catalog's card for a car the assistant picked: the rental's or the sale's slots, the same frame. */
const PickedCard: FC<{ car: ApiAssistantRentalCar | ApiAssistantSaleCar }> = ({ car }) => {
  const media = car.image ? (
    <ListingCardPhoto src={formatImageUrl(car.image)} alt={`${car.title}, photo`} />
  ) : (
    <ListingCardNoPhoto label={`${car.title}, no photo yet`} />
  );

  if (car.vertical === 'buy') {
    return (
      <ListingCard
        className={styles.card}
        media={media}
        title={car.title}
        price={
          <>
            <ListingCardPriceLabel>Price:</ListingCardPriceLabel>{' '}
            <ListingCardAmount>{formatMoney(car.price, car.currency)}</ListingCardAmount>
          </>
        }
        secondaryLine={saleSecondaryLine(car)}
        to={`/listing/${car.listingId}`}
      />
    );
  }

  // The price of the booking on the reply's dates, or the catalogue's without them (app#362).
  const price = assistantRentalPrice(car);

  return (
    <ListingCard
      className={styles.card}
      media={media}
      title={car.title}
      price={
        price ? (
          <>
            <ListingCardPriceLabel>Per day:</ListingCardPriceLabel>{' '}
            <ListingCardAmount>{price.amount}</ListingCardAmount>
            {price.days ? <ListingCardPriceLabel>{` · ${price.days}`}</ListingCardPriceLabel> : null}
            {price.saving ? (
              <span className={styles.saving}>
                {`${price.saving.band}: `}
                <span className={styles.savingFigure}>{price.saving.perDay}</span>
                {` ${price.saving.against}, `}
                <span className={styles.savingFigure}>{price.saving.total}</span>
              </span>
            ) : null}
          </>
        ) : undefined
      }
      secondaryLine={rentalSecondaryLine(car)}
      to={`/rentals/listing/${car.listingId}`}
    />
  );
};


interface AssistantChatProps {
  /** The catalog the window sits on: its words, its cards and its action; the api gets it with every message. */
  surface: AssistantSurface;
  /** The id the window's title carries, for the dialog's name. */
  titleId: string;
  onClose: () => void;
  /** A laptop's panel: the field takes the focus as soon as it is there (a phone's keyboard would pop over the sheet). */
  focusField?: boolean;
}

/**
 * The storefront assistant (api#381, api#425; the positioning note, decision 4), inside its window
 * (app#397, `AssistantWindow`): the person describes the car they need in plain words, the
 * assistant asks for what is missing, then shows up to three cars, each with why. On the rent
 * catalog the cars are free on the dates and "Book these dates" opens the car's page with the dates
 * filled in. On the sale catalog each car comes with its evidence card — what the listing states,
 * the VIN against it, the price against similar cars, how to buy — and "Prepare the offer" opens the
 * car's page with the delivery offer started and the city filled in; the person fills in the
 * recipient, sends the offer, pays and signs as before. The assistant never books, buys, pays or signs.
 *
 * The conversation is held above the routes (`AssistantConversationsProvider`): leaving for a car
 * marks the catalog's entry of the history, and Back brings the window back with it (`returnMark`).
 * An anonymous visitor asks too where the api serves them (`status.openToAnonymous`, api#441),
 * within their own daily limit; past it the field gives way to "Sign in to continue". Where the api
 * does not serve visitors, the window says "Sign in to ask", as before.
 */
export const AssistantChat: FC<AssistantChatProps> = ({ surface, titleId, onClose, focusField = false }) => {
  const copy = SURFACE_COPY[surface];
  const { isLoggedIn, isPending } = useSession();
  const status = useAssistantStatus();
  const conversation = useAssistantConversation(surface);
  const { entries, failure, pending, text, over } = conversation;
  const navigate = useNavigate();
  const touch = useMediaQuery(TOUCH_QUERY);
  const scrollBox = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const [more, setMore] = useState(false);

  // A visitor who has not signed in asks where the api serves them (api#441). There, a session still
  // being checked changes nothing: signed in or not, the person gets the field. Elsewhere it decides
  // between the field and the sign-in entry, and nothing is offered until it does.
  const canAsk = isLoggedIn || !!status.data?.openToAnonymous;
  const answered = entries.some((entry) => entry.role === 'assistant');
  const signIn = () => navigate('/auth', { state: { from: copy.signInFrom } });

  // Leaving the window for a car: Back is to bring the catalog back with the window open.
  const leave = (path: string) => {
    markReturnToAssistant(surface);
    navigate(path);
  };

  // A link of the conversation — a car's card, the dealer — leaves in this tab unless the person
  // asked for another (a modifier, or a link that opens a new tab of its own: the checks of a car).
  const onLinkClick = (event: MouseEvent<HTMLElement>) => {
    const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;

    if (!link || link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    markReturnToAssistant(surface);
  };

  // The panel opened while the session was still being checked has no field yet: it gets the focus
  // when it comes, unless the person has put the focus somewhere else meanwhile.
  const fieldShown = canAsk && !failure?.signIn && !over;

  useEffect(() => {
    const element = field.current;

    if (!focusField || !fieldShown || !element) return;
    if (document.activeElement === element.closest('[role="dialog"]')) element.focus({ preventScroll: true });
  }, [focusField, fieldShown]);

  // The newest line comes into view from its start: the person reads a reply from its first word.
  useLayoutEffect(() => {
    const box = scrollBox.current;
    const lines = box?.querySelectorAll<HTMLElement>('[data-line]');
    const last = lines?.[lines.length - 1];

    if (box && last && lines!.length > 1) box.scrollTop = Math.max(0, last.offsetTop - 8);
  }, [entries.length, pending]);

  // The conversation scrolls in a box of its own, and a finger's drag belongs to it only while part
  // of it is out of sight (app#292): with everything in view a drag it still allowed went to the page
  // behind the sheet in the Telegram Mini App on an iPhone. `data-more` opens the gesture.
  useEffect(() => {
    const box = scrollBox.current;

    if (!box || typeof ResizeObserver === 'undefined') return undefined;

    const look = () => setMore(box.scrollHeight - box.clientHeight > 1);
    const observer = new ResizeObserver(look);

    [box, ...Array.from(box.children)].forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  // On a laptop Enter sends and Shift+Enter is a new line, as in every chat; on a phone's keyboard
  // Enter stays a new line and the round button sends. Cmd/Ctrl+Enter sends everywhere, as before.
  // A word still being composed (an input method's Enter) is never sent.
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Safari ends a composition with an Enter whose `isComposing` is false and whose code is 229.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return;

    if (event.metaKey || event.ctrlKey || (!touch && !event.shiftKey && !event.altKey)) {
      event.preventDefault();
      void conversation.submit();
    }
  };

  const takeExample = (example: string) => {
    conversation.setText(example);
    field.current?.focus();
  };

  return (
    <div className={styles.chat}>
      <div className={styles.header}>
        <Sparkle size={20} color="var(--gac-accent)" aria-hidden />
        <h2 id={titleId} className={styles.title}>
          {ASSISTANT_TITLE}
        </h2>
        {answered || over ? (
          <button type="button" className={styles.textButton} disabled={pending} onClick={conversation.startOver}>
            New conversation
          </button>
        ) : null}
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          <X size={20} aria-hidden />
        </button>
      </div>

      <div ref={scrollBox} className={styles.scroll} data-more={more ? '' : undefined}>
        <ol className={styles.log} role="log" aria-live="polite" aria-label="Conversation with the assistant" onClickCapture={onLinkClick}>
          {/* The assistant's rules come first, as its first message. */}
          <li className={classNames(styles.entry, styles.assistant)} data-line>
            <Typography color="white" className={styles.bubble}>
              {copy.promise}
            </Typography>
          </li>
          <ConversationBoundary key={entries.length === 0 ? 'clean' : 'kept'} onFail={conversation.startOver}>
          {entries.map((entry) => (
            <ConversationLine key={entry.id} entry={entry} leave={leave} />
          ))}
          </ConversationBoundary>
          {pending ? (
            <li className={classNames(styles.entry, styles.assistant)} data-line>
              <Typography color="lightWhite" className={styles.bubble}>
                {copy.pending}
              </Typography>
            </li>
          ) : null}
        </ol>
        {canAsk && entries.length === 0 && !pending && !failure ? (
          <div className={styles.examples} role="group" aria-label="Examples">
            {copy.examples.map((example) => (
              <button key={example} type="button" className={styles.example} onClick={() => takeExample(example)}>
                {example}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {failure || (over && canAsk) ? (
        <div className={styles.failure} role="status">
          <Typography color="white" variant="small">
            {failure?.text ?? 'This conversation took every message it may. Start a new one.'}
          </Typography>
        </div>
      ) : null}

      {canAsk ? (
        failure?.signIn ? (
          <Button title="Sign in to continue" variant="accent" textColor="accentInk" className={styles.wide} onClick={signIn} />
        ) : over ? null : (
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault();
              void conversation.submit();
            }}
          >
            <textarea
              ref={field}
              className={styles.field}
              aria-label={ASSISTANT_FIELD_NAME}
              placeholder={ASSISTANT_FIELD_NAME}
              rows={1}
              maxLength={ASSISTANT_TEXT_LIMIT}
              value={text}
              disabled={pending}
              onChange={(event) => conversation.setText(event.target.value)}
              onKeyDown={onKeyDown}
            />
            <button type="submit" className={styles.send} aria-label="Send" disabled={!text.trim() || pending}>
              <PaperPlaneTilt size={20} weight="fill" aria-hidden />
            </button>
          </form>
        )
      ) : isPending ? null : (
        <Button title="Sign in to ask" variant="white" className={styles.wide} onClick={signIn} />
      )}
    </div>
  );
};

/**
 * One line of the conversation, drawn as a component of its own so that whatever its data does
 * while it is drawn happens inside `ConversationBoundary` — a line kept in the tab's storage is the
 * browser's data, and a callback of the chat's own render would throw past the boundary.
 */
const ConversationLine: FC<{ entry: ConversationEntry; leave: (path: string) => void }> = ({ entry, leave }) => {
  // The dates the cars can be booked on, when the api says so: every card gets the action.
  const bookable = entry.action.type === 'book' ? entry.action.dates : null;
  // Cars for sale: every card opens the car's page with the offer started, in the city named if any.
  const offer = entry.action.type === 'prepareOffer' ? entry.action : null;

  return (
    <li
      className={classNames(styles.entry, entry.role === 'person' ? styles.person : styles.assistant)}
      data-line
    >
      <Typography color={entry.role === 'person' ? 'lightWhite' : 'white'} className={styles.bubble}>
        {entry.text}
      </Typography>
      {entry.cars.length > 0 ? (
        <ul className={styles.picks} aria-label="Cars the assistant picked">
          {entry.cars.map((pick) => (
            <li key={pick.listingId} className={styles.pick}>
              <PickedCard car={pick.car} />
              <Typography color="lightWhite" variant="small" className={styles.reason}>
                {pick.reason}
              </Typography>
              {pick.car.vertical === 'buy' && pick.generation ? (
                // The model's general knowledge, labelled by the platform (api#430): never a card line.
                <Typography color="lightWhite" variant="small" className={styles.reason}>
                  {`General information, check the car’s page: ${pick.generation}`}
                </Typography>
              ) : null}
              {pick.car.vertical === 'buy' ? <EvidenceCard car={pick.car} action={entry.action} /> : null}
              {pick.car.vertical === 'rent' && pick.car.trip ? (
                <dl className={styles.evidence} aria-label={`Trip cost: ${pick.car.title}`}>
                  <div className={classNames(styles.evidenceRow, styles['tone-plain'])}>
                    <dt>
                      <Typography as="span" color="lightWhite" variant="small" fontWeight="600">
                        Trip cost
                      </Typography>
                    </dt>
                    <dd>
                      <Typography as="span" color="white" variant="small">
                        {tripCostText(pick.car.trip)}
                      </Typography>
                    </dd>
                  </div>
                </dl>
              ) : null}
              {bookable ? (
                <Button
                  title="Book these dates"
                  aria-label={`Book these dates: ${pick.car.title}`}
                  variant="accent"
                  textColor="accentInk"
                  className={styles.book}
                  onClick={() => leave(bookingPathWithDates(pick.listingId, bookable))}
                />
              ) : null}
              {offer && pick.car.vertical === 'buy' && pick.car.evidence?.purchase.buyable !== false ? (
                <Button
                  title="Prepare the offer"
                  aria-label={`Prepare the offer: ${pick.car.title}`}
                  variant="accent"
                  textColor="accentInk"
                  className={styles.book}
                  onClick={() => leave(offerPathWithPoint(pick.listingId, offer.deliveryPointId))}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
};

/**
 * The last guard of a kept conversation (spec §4, p. 2): a line the tab's storage brought back that
 * passed its checks and still cannot be drawn — an evidence card of a shape no reply has — ends the
 * conversation instead of the page: a clean sheet, never an error screen.
 */
class ConversationBoundary extends Component<{ onFail: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn('The assistant’s conversation could not be drawn and was started over.', error, info.componentStack);
    this.props.onFail();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}
